import React from "react";

import instance from "@store/middleware/api";

import { colors, fonts, fontSizes } from "@/styles/design-system";

/**
 * Dev-only auth toggle — flips the session between logged-in (as the
 * premium test account) and logged-out without hitting the dropdown
 * menu. Sibling to `DevUserSwitcher` (which switches *between* logged-in
 * accounts). Gated behind NODE_ENV !== "production".
 *
 * Password comes from REACT_APP_DEV_SUB_PASSWORD (local .env only) — never
 * hardcoded, so no plaintext credential ships in the bundle (2026-07-02 audit).
 */
const DEV_LOGIN = { email: "hoyaw23479@seosnaps.com", password: process.env.REACT_APP_DEV_SUB_PASSWORD || "" };
const DEV_ACTIVE_ACCOUNT_KEY = "dev-active-test-account";

const isDev = process.env.NODE_ENV !== "production";

const readToken = () => {
  try {
    const raw = localStorage.getItem("accessToken");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.value || null;
  } catch {
    return null;
  }
};

const DevAuthSwitcher: React.FC = () => {
  if (!isDev) return null;

  const [pending, setPending] = React.useState<"in" | "out" | null>(null);

  const loggedIn = typeof window !== "undefined" ? !!localStorage.getItem("accessToken") : false;

  const logIn = async () => {
    setPending("in");
    try {
      const { data } = await instance.post("/auth/login", DEV_LOGIN);
      const now = Date.now();
      localStorage.setItem("accessToken", JSON.stringify({ value: data.accessToken, expiry: now + data.accessTokenTtl * 1000 }));
      localStorage.setItem("refreshToken", JSON.stringify({ value: data.refreshToken, expiry: now + data.refreshTokenTtl * 1000 }));
      localStorage.setItem(DEV_ACTIVE_ACCOUNT_KEY, "premium");
      window.location.reload();
    } catch (e) {
      setPending(null);
      console.error(e);
    }
  };

  const logOut = async () => {
    setPending("out");

    // Best-effort server-side invalidate. Failure is fine — we still
    // clear local tokens below.
    try {
      const accessToken = readToken();
      const refreshRaw = localStorage.getItem("refreshToken");
      const refreshToken = refreshRaw ? (JSON.parse(refreshRaw)?.value || null) : null;
      if (accessToken && refreshToken) {
        await instance.post("/auth/logout", { accessToken, refreshToken });
      }
    } catch { /* ignore */ }

    // Mirror the logout reducer: preserve cart + sub-action-alert keys.
    const cart = localStorage.getItem("as-cart");
    const subAlerts: Record<string, string> = {};
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith("sub-action-alert-")) {
        const v = localStorage.getItem(k);
        if (v !== null) subAlerts[k] = v;
      }
    }
    localStorage.clear();
    if (cart) localStorage.setItem("as-cart", cart);
    for (const [k, v] of Object.entries(subAlerts)) localStorage.setItem(k, v);

    window.location.reload();
  };

  const btnStyle = (active: boolean, isPending: boolean): React.CSSProperties => ({
    padding: "3px 10px",
    borderRadius: 6,
    border: "none",
    cursor: active || isPending ? "default" : "pointer",
    fontFamily: fonts.body,
    fontSize: fontSizes.xs,
    background: active ? "rgba(156, 211, 253, 0.2)" : "rgba(255, 255, 255, 0.06)",
    color: active ? colors.accent.blue : "rgba(251, 244, 236, 0.5)",
  });

  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "4px 8px", borderRadius: 8,
      background: "rgba(255, 255, 255, 0.03)", border: "1px dashed rgba(255, 255, 255, 0.15)",
      fontFamily: fonts.body, fontSize: fontSizes.xs, color: "rgba(251, 244, 236, 0.5)",
    }}>
      <span>DEV auth</span>
      <button
        onClick={() => !loggedIn && pending !== "in" && logIn()}
        disabled={loggedIn || pending === "in"}
        style={btnStyle(loggedIn, pending === "in")}
      >{pending === "in" ? "…" : "Logged in"}</button>
      <button
        onClick={() => loggedIn && pending !== "out" && logOut()}
        disabled={!loggedIn || pending === "out"}
        style={btnStyle(!loggedIn, pending === "out")}
      >{pending === "out" ? "…" : "Logged out"}</button>
    </div>
  );
};

export default DevAuthSwitcher;
